import { batch } from 'solid-js'
import { modifyMutable, produce } from 'solid-js/store'
import { Vector2 } from 'three'
import { useIsEngineModeMapPreviewMemo } from '../../../rx/memo/useIsEngineModeMapPreviewMemo'
import { useMouseDownEventSignal } from '../../../rx/signal/useMouseDownEventSignal'
import { useMouseDownPositionSignal } from '../../../rx/signal/useMouseDownPositionSignal'
import { useSelectedToolSignal } from '../../../rx/signal/useSelectedToolSignal'
import { useMouseDragList } from '../../../rx/use/useMouseDragList'
import { Engine } from '../../core/engine/Engine.type'
import resetEngineToDefaultSelectedTool from '../../core/engine/resetEngineToDefaultSelectedTool'
import updateSelectedPosition from '../../core/engine/SelectionMode/updateSelectedPosition'
import calculateSelectedPositionEnts from '../../core/entity/calculateSelectedPositionEnts'
import getEntitiesAtPosition from '../../core/entity/getEntitiesAtPosition'
import { SelectedToolId } from '../../core/map_editor/SelectedToolId.enum'
import { createEraseTileAction } from '../../core/state/flux/action/Map/EraseTileAction'
import { createPaintTileAction } from '../../core/state/flux/action/Map/PaintTileAction'
import dispatchClient from '../../core/state/flux/dispatchClient'
import { samePosition } from '../../core/tile_position_xy/samePosition'
import toCoord from '../../core/tile_position_xy/toCoord'
import { byLayerIdDesc } from '../../core/util/id'
import { abs } from '../../core/util/math/Math'
import MouseDragAgg from '../../core/view_ctx/MouseDragAgg'
import { emitEvent } from '../../event/event'
import { actionBindings } from '../../event/InputRouter/actionBindings'
import getInputActionListenerList from '../../event/InputRouter/getInputActionListenerList'
import mouseClickEventMatchesInputActionBinding from '../../event/InputRouter/mouseClickEventMatchesInputActionBinding'
import onElementEvent from '../../onElementEvent'
import useGetTileNodeFromPointer from '../../three/fn/useGetTileNodeIntersectionFromPointer'
import isRightClick from './isRightClick'
import { usePaintTileOptionSignal } from '../../../rx/signal/usePaintTileOptionSignal'
import { useEraseTileOptionSignal } from '../../../rx/signal/useEraseTileOptionSignal'
import { useSelectedPlayerIdSignal } from '../../../rx/signal/useSelectedPlayerIdSignal'
import { useDraftMoveSignal } from '../../../rx/signal/useDraftMoveSignal'

export default function registerMouseUpEvent(
  engine: Engine,
  elem: HTMLElement,
) {
  const getTileNodeIntersectionFromPointer = useGetTileNodeFromPointer(engine)
  const isEngineModeMapPreview = useIsEngineModeMapPreviewMemo(engine)
  const [getSelectedTool] = useSelectedToolSignal(engine)
  const [getMouseDownEvent, setMouseDownEvent] = useMouseDownEventSignal(engine)
  const [getMouseDownPosition, setMouseDownPosition] = useMouseDownPositionSignal(engine)
  const [getDraftMove] = useDraftMoveSignal(engine)
  const mouseDragList = useMouseDragList(engine)

  // console.log('registerMouseUpEvent')
  function onMouseUp(event: MouseEvent) {
    // console.log('onMouseUp', event)
    // engine.mouseUpEvent = event
    const { offsetX, offsetY } = event
    const draftMove = getDraftMove()
    const mouseDownPosition = getMouseDownPosition()
    const selectedTool = getSelectedTool()
    const mouseDownEvent = getMouseDownEvent()

    // const isMap = wasEventTargetMap(event)
    const isDrag: boolean = mouseDragMetThreshold(mouseDragList)

    // a long press should be treated like a right click
    // right click is undefined behavior
    // @todo make isLongPress not true for pans
    const clickDuration = mouseDownEvent ? event.timeStamp - mouseDownEvent.timeStamp : null
    const isLongPress = clickDuration ? clickDuration > 500 : false
    // a click is a mouse down + mouse up event that occurs in less than 500 ms
    const isClick = mouseDownEvent && !isLongPress && !isDrag
    // console.log('isClick', isClick)

    // selectedTool is now obtained via getSelectedTool() at the start of the function

    const pointerX = (offsetX / (elem as HTMLCanvasElement).clientWidth) * 2 - 1
    const pointerY = - (offsetY / (elem as HTMLCanvasElement).clientHeight) * 2 + 1

    const mouseUpIntersection = getTileNodeIntersectionFromPointer(new Vector2(pointerX, pointerY))
    const mouseUpTile = mouseUpIntersection?.object?.userData?.tile
    const mouseUpPosition = mouseUpTile ? toCoord(mouseUpTile) : null
    // console.log('onMouseUp.position', mouseUpPosition, mouseUpVector3, {offsetX, offsetY, isClick})
    // if (isLongPress) {
    //   console.log('onMouseUp.isLongPress', {
    //     'selectedTool === SelectedToolId.Inspect': selectedTool === SelectedToolId.Inspect,
    //     selectedTool,
    //   })
    // } else
    if (isClick) {
      // console.log('onMouseUp.isClick')
      if (isRightClick(event)) {
        // console.log('onMouseUp.isRightClick', true)
        modifyMutable(engine, produce(resetEngineToDefaultSelectedTool))
      } else if (isEngineModeMapPreview()) {
        // do nothing
      } else if (draftMove) {
        // console.log('isClick.draftMove', deepClone(draftMove))
        // if (draftMove.destPosition)
      } else if (selectedTool === SelectedToolId.Inspect) {
        // console.log('isClick.SelectedToolId.Inspect')
        if (mouseDownEvent.dontSelect) {
          // dont set a selectedPosition if the mouseDown was an attack
        } else {
          modifyMutable(
            engine,
            produce((engine: Engine) => {
              updateSelectedPosition(engine, mouseUpPosition)
              calculateSelectedPositionEnts(engine)
            })
          )
        }
      } else if (selectedTool === SelectedToolId.Paint) {
        // console.log('isClick.SelectedToolId.Paint')
        // console.log('mouseUpPosition', {
        //   result: mouseUpPosition && samePosition(mouseDownPosition, mouseUpPosition),
        //   mouseUpPosition,
        //   mouseDownPosition,
        // })
        if (mouseUpPosition && samePosition(mouseDownPosition, mouseUpPosition)) {
          const [getPaintTileOption] = usePaintTileOptionSignal(engine)
          const [getSelectedPlayerId] = useSelectedPlayerIdSignal(engine)
          dispatchClient(
            engine,
            createPaintTileAction(getPaintTileOption(), mouseUpPosition, getSelectedPlayerId())
          )
        }
      } else if (selectedTool === SelectedToolId.Erase) {
        // console.log('isClick.SelectedToolId.Erase')
        if (mouseUpPosition && samePosition(mouseDownPosition, mouseUpPosition)) {
          const [getEraseTileOption] = useEraseTileOptionSignal(engine)
          dispatchClient(engine, createEraseTileAction(getEraseTileOption(), mouseUpPosition))
        }
      } else if (selectedTool === SelectedToolId.Players) {
        // console.log('isClick.SelectedToolId.Players')
        if (mouseUpPosition && samePosition(mouseDownPosition, mouseUpPosition)) {
          const stack = getEntitiesAtPosition(engine.state.ents, mouseUpPosition)
          stack.sort(byLayerIdDesc)
          const playerEnt = stack.find((ent) => ent.player_id && !ent.taxi_id)
          const [, setSelectedPlayerId] = useSelectedPlayerIdSignal(engine)
          setSelectedPlayerId(playerEnt?.player_id || null)
          // dispatchClient(engine, createEraseTileAction(engine.eraseToolOption, mouseUpPosition))
        }
      } else {
        // console.log('isClick.SelectedToolId.Else')
        for (let i = 0; i < actionBindings.length; i++) {
          const { action, input } = actionBindings[i]
          // console.log('onMouseUp.isClick.isMatch', mouseClickEventMatchesInputActionBinding(event, input), actionBindings[i])
          if (mouseClickEventMatchesInputActionBinding(event, input)) {
            emitEvent(getInputActionListenerList(action), event)
            // console.log('onMouseUp.isClick.isMatch.true.return')
            return
          }
        }
      }
    } else {
      // console.log('onMouseUp.!isClick')
    }

    batch(() => {
      setMouseDownEvent(null)
      setMouseDownPosition(null)
      mouseDragList.length = 0
    })
  }

  // onWindowEvent('mouseup', onMouseUp)
  onElementEvent(elem, 'mouseup', onMouseUp)
}

function mouseDragMetThreshold(
  mouseDragList: Readonly<Array<MouseEvent>> | null,
) {
  const pixelThreshold: number = 32
  const threshMult = 6
  const agg = mouseDragListToAgg(mouseDragList)
  if (!agg) return false
  if (abs(agg.x * threshMult) > pixelThreshold) return true
  if (abs(agg.y * threshMult) > pixelThreshold) return true
  return false
}

function mouseDragListToAgg(
  mouseDragList: Readonly<Array<MouseEvent>> | null
): Readonly<MouseDragAgg | null> {
  const list = mouseDragList
  if (!(list && list.length > 0)) return null
  let pan_x = 0,
    pan_y = 0
  for (let index = 0; index < list.length; index++) {
    const mmEvent = list[index]
    pan_x += mmEvent.movementX
    pan_y += mmEvent.movementY
  }
  return { x: pan_x, y: pan_y }
}
